import { Loading } from "@/components/loading/Loading.tsx";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { ApiStandardError } from "@/modules/api/ApiError.ts";
import type { AddMethodProps } from "@/modules/billing/choose_method/AddMethodParams.ts";
import { addCardRequest } from "@/modules/billing/choose_method/addMethodUtils.ts";
import { FormBox } from "@/modules/billing/choose_method/FormBox.tsx";
import { ErrorText } from "@/modules/billing/payment_forms/components.tsx";
import { paypalLoader } from "@/modules/billing/paypal/paypalLoader.ts";
import * as queries from "@/modules/billing/paypal/paypalQuery.ts";
import { PayPalVaultLoadError, type ExtendedCardFieldsOnApproveData } from "@/modules/billing/paypal/utils.ts";
import { t_SavingInformation } from "@/modules/billing/progressLabels.tsx";
import { ErrorMessage } from "@/modules/error/ErrorFallback.tsx";
import { useUnmountSignal } from "@/utils/react/useUnmountSignal.tsx";
import { Trans, useLingui } from "@lingui/react/macro";
import type { PayPalButtonsComponent } from "@paypal/paypal-js";
import type { PaypalVaultConfig } from "@warrenio/api-spec/spec.oats.gen";
import { useEffect, useImperativeHandle, useRef, useState } from "react";

export default function PaypalVaultAddForm({
    actionsRef,
    config: { client_id },
}: { config: PaypalVaultConfig } & AddMethodProps) {
    //#region Hooks
    const apiClient = useApiClient();
    const { t } = useLingui();
    const signal = useUnmountSignal();

    const [loadError, setLoadError] = useState<PayPalVaultLoadError | undefined>(undefined);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const paypalButtons = useRef<PayPalButtonsComponent | undefined>(undefined);

    const paypalSessionId = useRef<string | undefined>(undefined);
    const paypalPaymentToken = useRef<string | undefined>(undefined);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        (async () => {
            let paypal;
            let sessionId;
            try {
                const loadResult = await paypalLoader({ apiClient, signal, clientId: client_id });
                paypal = loadResult.paypal;
                sessionId = loadResult.sessionId;
                if (!paypal.Buttons) {
                    throw new PayPalVaultLoadError("paypal.Buttons not found");
                }
            } catch (error) {
                const paypalError = new PayPalVaultLoadError(undefined, { cause: error });
                setLoadError(paypalError);
                setLoading(false);
                throw paypalError;
            }

            paypalSessionId.current = sessionId;
            // abort if paypalButtons (paypalButtons.current) is already initiated
            if (paypalButtons.current) return;

            paypalButtons.current = paypal.Buttons({
                createVaultSetupToken: async () => {
                    try {
                        const result = await queries.setupTokenQuery(apiClient, signal, sessionId);
                        console.debug("PayPal setup token response", result);
                        return result.token;
                    } catch (e: unknown) {
                        const msg = e instanceof ApiStandardError ? e.errorMessage : t`Problem with PayPal API.`;
                        setError(msg);
                        return "";
                    }
                },
                onApprove: async (data) => {
                    setError("");
                    const vaultSetupToken = (data as ExtendedCardFieldsOnApproveData).vaultSetupToken!;
                    try {
                        const result = await queries.paymentTokenQuery(apiClient, signal, sessionId, vaultSetupToken);
                        console.debug("PayPal payment token response", result);
                        paypalPaymentToken.current = result.token;
                    } catch (e: unknown) {
                        const msg = e instanceof ApiStandardError ? e.errorMessage : t`Problem with PayPal API.`;
                        setError(msg);
                    }
                },
                onError: (error) => {
                    console.log(`Error: ${error.message}`);
                },
            });

            try {
                await paypalButtons.current.render("#paypal-vault-button");
                setLoading(false);
            } catch (error) {
                console.log(`Error: ${error}`);
            }
        })();
        // NB: We should only try to load the PayPal SDK once, so do not add any dependencies to the effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(actionsRef, () => ({
        // eslint-disable-next-line @typescript-eslint/require-await
        async validate() {
            const isValid = !!paypalPaymentToken.current;
            if (!isValid) {
                setError(t`Connect to PayPal first`);
            }
            return isValid;
        },
        needsPopUp() {
            return false;
        },
        async addPaymentMethod({ account, progress }) {
            progress(t_SavingInformation);
            await addCardRequest(apiClient, {
                billing_account_id: account.id,
                session_id: paypalSessionId.current,
                token: paypalPaymentToken.current!,
                processor_keyword: "paypal_vault",
            });
        },
    }));
    //#endregion

    return (
        <FormBox>
            {loadError && <ErrorMessage error={loadError} />}
            {error && (
                <div className="pb-3">
                    <ErrorText text={error} />
                </div>
            )}

            {loading && <Loading icon="none" fillSpace />}
            <div className={loading ? "hidden" : undefined}>
                <p className="pb-1">
                    <Trans>Click the button to connect to PayPal.</Trans>
                </p>
                <div id="paypal-vault-button" />
            </div>
        </FormBox>
    );
}
